/**
 * Who is on shift right now (#2175 C5). Port of the people list of
 * mobile-management/src/features/cockpit/OnShiftNowRow.tsx and of
 * `activeShifts` / `shiftsRunningAt` in mobile-management/src/features/team/staffing.ts.
 *
 * A shift counts unless it is CANCELLED (the coverage rule) and while the
 * current minute lies inside it; a shift whose end is not after its start runs
 * past midnight and counts until 24:00. Each person is listed once (a split or
 * multi-group shift names them once), with the first name (or the last name)
 * and the first group of the first running shift. A shift without a name is
 * skipped. Presence (#2114) only annotates: `inGroup` is null while the
 * organisation does not record group presence.
 */

export interface OnShiftShift {
  employee_id?: string;
  start_time?: string;
  end_time?: string;
  status?: string;
  employee?: { firstname?: string; lastname?: string };
  groups?: { id?: string; name?: string }[];
}

export interface OnShiftPerson {
  employeeId: string;
  name: string;
  group?: string;
  /** In the group now (presence row open); null while presence is not recorded. */
  inGroup: boolean | null;
}

/** Names spelled out before the rest collapses into "+N more". */
export const ON_SHIFT_NAMES_SHOWN = 3;

function minutesOf(time: string | undefined): number {
  const [h, m] = (time ?? "").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** Shifts running at `nowMinutes` (minutes since local midnight), cancelled ones excluded. */
export function shiftsRunningAt<S extends OnShiftShift>(shifts: readonly S[] | undefined, nowMinutes: number): S[] {
  return (shifts ?? []).filter((s) => {
    if (s.status === "CANCELLED") return false;
    const start = minutesOf(s.start_time);
    const rawEnd = minutesOf(s.end_time);
    const end = rawEnd > start ? rawEnd : 24 * 60;
    return nowMinutes >= start && nowMinutes < end;
  });
}

/**
 * The people on shift now. `inScope` narrows by group: a shift counts when one
 * of its groups is in scope; a shift without a group only when no scope is set
 * (`inScope` undefined). `inGroup` is the set of employees with an open
 * presence row, or undefined while the organisation does not record presence.
 */
export function onShiftNow(
  shifts: readonly OnShiftShift[] | undefined,
  nowMinutes: number,
  options: { inScope?: (groupId: string) => boolean; inGroup?: ReadonlySet<string> } = {},
): OnShiftPerson[] {
  const { inScope, inGroup } = options;
  const seen = new Map<string, OnShiftPerson>();
  for (const s of shiftsRunningAt(shifts, nowMinutes)) {
    const id = s.employee_id;
    if (!id || seen.has(id)) continue;
    const groups = s.groups ?? [];
    if (inScope) {
      if (groups.length === 0 || !groups.some((g) => !!g.id && inScope(g.id))) continue;
    }
    const name = s.employee?.firstname?.trim() || s.employee?.lastname?.trim() || "";
    if (!name) continue;
    seen.set(id, {
      employeeId: id,
      name,
      group: groups[0]?.name || undefined,
      inGroup: inGroup ? inGroup.has(id) : null,
    });
  }
  return [...seen.values()];
}
